/**
 * @file 運動チェックの種目カタログ。
 *
 * @what: 『0〜5歳児の発達に合った 楽しい！運動あそび』の遊び単位の項目を、考査のテーマ別に
 *   まとめて持つ。id は StudyProgressLog.motor_item_id の値。page は本のページ番号で、
 *   本だな (source id `undou-asobi-0-5`) の該当ページへ飛ぶのに使う。
 * @why: 毎日やるのは本の遊びそのものなので、チェックも遊び単位で付ける (代表 2026-08-24。
 *   考査の種目単位から変更)。収録は週メニュー (幹・発展・A/B/C) の遊びと、2 歳の親子あそび
 *   全 41 件 (かんたんに下げる先・雨の日用。代表 2026-08-24「0〜2歳の内容も残す」)。
 *   0〜1 歳台 (首すわり〜あんよ期) と本のそれ以外の遊びは載せない (毎日の画面に出す量ではない)。
 * @failure: 描画専用の定数。id を変えると過去の記録と切れるので、変えるときは移行を考える。
 *   page は紙のページ番号 = 本だなのページ番号 (取り込みの不変条件)。
 */

export interface MotorItem {
  /** StudyProgressLog.motor_item_id の値 (^[a-z0-9_]+$)。 */
  id: string;
  label: string;
  /** 『0〜5歳児の…運動あそび』の紙のページ番号。コース化など本の 1 ページに紐づかない項目は無し。 */
  page?: number;
  /** 本の年齢の目安 (巻末の早見表から。同名の遊びはページで同定した)。 */
  age?: string;
}

export interface MotorGroup {
  id: string;
  /** 考査のテーマ (週メニューの対照表と同じ表記)。 */
  label: string;
  /** 主に身につく力。 */
  force: string;
  items: readonly MotorItem[];
}

/** 本だなでのこの本の資料 id (取り込み時に採番)。ページへのリンク先の解決に使う。 */
export const UNDOU_ASOBI_SOURCE_ID = "undou-asobi-0-5";

/**
 * 週メニュー (考査対照表つき) の本だなの資料 id。
 * @why: うんどうタブから「週メニュー / 対照表 p.2 / 2歳の親子あそび p.4」へ 1 タップで
 *   飛ぶための鍵。取り込み時に採番した id で、本だなに無い環境では導線ごと出さない。
 */
export const UNDOU_MENU_SOURCE_ID = "undou-menu";

export const MOTOR_GROUPS: readonly MotorGroup[] = [
  {
    id: "kuma",
    label: "クマ歩き・クモ歩き",
    force: "支持力",
    items: [
      { id: "kumasan_aruki", label: "クマさん歩き", page: 105, age: "3〜4歳" },
      { id: "wanwan_inu_aruki", label: "ワンワン犬歩き", page: 104, age: "3〜4歳" },
      { id: "kani_yoko_aruki", label: "カニの横歩き", page: 128, age: "3〜4歳" },
      { id: "kenken_kumasan", label: "ケンケンクマさん", page: 106, age: "4〜5歳" },
      { id: "ryoashi_teoshiguruma", label: "両足手押し車", page: 136, age: "4〜5歳" },
      { id: "azarashi_ebi_pose", label: "アザラシとエビのポーズ", page: 89, age: "2歳" },
      { id: "haihai_de_touch", label: "ハイハイでタッチ", page: 95, age: "2歳" },
    ],
  },
  {
    id: "kenpa",
    label: "ケンパー・ケンケン",
    force: "跳躍力・バランス感覚",
    items: [
      { id: "gupa_tobi", label: "グーパー跳び", page: 147, age: "3〜4歳" },
      { id: "kenpa_hoop_watari", label: "ケンパーでフープ渡り", page: 213, age: "3〜4歳" },
      { id: "kataashi_jump_goe", label: "片足ジャンプ越え", page: 169, age: "3〜4歳" },
      { id: "kenken_tobi", label: "ケンケン跳び", page: 149, age: "4〜5歳" },
      { id: "iroiro_gupa_tobi", label: "いろいろグーパー跳び", page: 149, age: "4〜5歳" },
      { id: "jump_de_touch", label: "ジャンプでタッチ！", page: 81, age: "2歳" },
      { id: "oonami_konami_jump", label: "大波小波をジャンプ", page: 87, age: "2歳" },
      { id: "pyonpyon_jump", label: "ピョンピョンジャンプ", page: 88, age: "2歳" },
    ],
  },
  {
    id: "skip",
    label: "スキップ・ギャロップ",
    force: "リズム感覚",
    items: [
      { id: "rhythmical_jump", label: "リズミカルにジャンプ", page: 102, age: "3〜4歳" },
      { id: "kaeru_pyonpyon", label: "カエルぴょんぴょん", page: 230, age: "3〜4歳" },
      { id: "skip_ranran", label: "スキップランラン", page: 231, age: "4〜5歳" },
      { id: "yoko_gallop", label: "横にギャロップ", page: 231, age: "4〜5歳" },
      { id: "omoikkiri_skip", label: "思いっ切りスキップ", page: 232, age: "4〜5歳" },
      { id: "rhythm_ashibumi", label: "リズムにのって足踏み", page: 79, age: "2歳" },
      { id: "tetsunagi_gupa", label: "手つなぎグーパー", page: 93, age: "2歳" },
    ],
  },
  {
    id: "mat",
    label: "前転・マット",
    force: "回転感覚",
    items: [
      { id: "satsumaimo_gorogoro", label: "さつまいもゴロゴロ", page: 131, age: "3〜4歳" },
      { id: "gorogoro_imomushi", label: "ゴロゴロいも虫", page: 131, age: "3〜4歳" },
      { id: "zenten", label: "前転", page: 139, age: "4〜5歳" },
      { id: "tori_habatakou", label: "鳥になってはばたこう", page: 76, age: "2歳" },
      { id: "onaka_de_kururin", label: "お腹でくるりん", page: 90, age: "2歳" },
      { id: "ushiro_ni_kororin", label: "後ろにコロリン", page: 95, age: "2歳" },
    ],
  },
  {
    id: "heikindai",
    label: "平均台・バランスストーン",
    force: "バランス感覚",
    items: [
      { id: "mat_hiza_watari", label: "マットのひざ渡り", page: 218, age: "3〜4歳" },
      { id: "futoi_ipponbashi", label: "太い一本橋渡り", page: 219, age: "3〜4歳" },
      { id: "tekkyou_watari", label: "鉄橋渡り", page: 220, age: "3〜4歳" },
      { id: "ryote_tsuki_tobikoshi", label: "両手つき跳び越し", page: 222, age: "4〜5歳" },
      { id: "hebisan_tsunawatari", label: "ヘビさん綱渡り", page: 80, age: "2歳" },
      { id: "tsunawatari_oikakekko", label: "綱渡りで追いかけっこ", page: 80, age: "2歳" },
      { id: "marui_senro_densha", label: "丸い線路で電車ごっこ", page: 82, age: "2歳" },
      { id: "soft_heikindai_tsunawatari", label: "ソフト平均台で綱渡り", page: 85, age: "2歳" },
      { id: "kumasan_marutagoe", label: "クマさん歩きで丸太越え", page: 93, age: "2歳" },
    ],
  },
  {
    id: "kataashi",
    label: "片足バランス",
    force: "バランス感覚",
    items: [
      { id: "balance_dainoji", label: "バランス大の字", page: 120, age: "3〜4歳" },
      { id: "tombo_henshin", label: "トンボさんに変身", page: 230, age: "3〜4歳" },
      { id: "kenken_mukadetai", label: "ケンケンムカデ隊", page: 241, age: "4〜5歳" },
      { id: "kurukuru_dance", label: "くるくるダンス", page: 79, age: "2歳" },
      { id: "senaka_no_ue_ottotto", label: "背中の上でおっとっと", page: 84, age: "2歳" },
      { id: "chikara_ippai_osou", label: "力いっぱい押そう", page: 85, age: "2歳" },
      { id: "ashi_no_ue_ottotto", label: "足の上でおっとっと", page: 86, age: "2歳" },
      { id: "kataashi_de_pyonpyon", label: "片足でピョンピョン", page: 89, age: "2歳" },
      { id: "balance_kyousou", label: "バランス競争", page: 90, age: "2歳" },
      { id: "back_orai", label: "バックオーライ", page: 92, age: "2歳" },
    ],
  },
  {
    id: "nageru",
    label: "ボール投げ・的当て",
    force: "空間認知力",
    items: [
      { id: "mataate_asobi", label: "的当てあそび", page: 200, age: "3〜4歳" },
      { id: "ball_trampoline", label: "ボールのトランポリン", page: 198, age: "3〜4歳" },
      { id: "wa_wo_karada_catch", label: "輪を体でキャッチ！", page: 78, age: "2歳" },
      { id: "tissue_catch", label: "ティッシュをキャッチ！", page: 82, age: "2歳" },
    ],
  },
  {
    id: "dribble",
    label: "ドリブル・キャッチ",
    force: "リズム感覚・協応性",
    items: [
      { id: "ookiku_bound_catch", label: "大きくバウンドキャッチ", page: 203, age: "3〜4歳" },
      { id: "chiisaku_bound_catch", label: "小さくバウンドキャッチ", page: 202, age: "3〜4歳" },
      { id: "aruite_dribble", label: "歩いてドリブル", page: 204, age: "4〜5歳" },
      { id: "hashitte_dribble", label: "走ってドリブル", page: 204, age: "4〜5歳" },
    ],
  },
  {
    id: "tobu",
    label: "立ち幅跳び・ゴム跳び",
    force: "跳躍力",
    items: [
      { id: "nawa_ni_jump", label: "なわに向かってジャンプ！", page: 150, age: "3〜4歳" },
      { id: "nawa_takatobi", label: "なわ高跳び", page: 151, age: "3〜4歳" },
      { id: "tsukamari_tobi", label: "つかまり跳び", page: 122, age: "3〜4歳" },
      { id: "hoop_watari_kangaroo", label: "フープ渡り・カンガルー跳び", page: 213, age: "3〜4歳" },
    ],
  },
  {
    id: "tetsubou",
    label: "鉄棒・ぶら下がり",
    force: "懸垂力・逆さ感覚",
    items: [
      { id: "osaru_burasagari", label: "おサルさんのぶら下がり", page: 180, age: "3〜4歳" },
      { id: "yurayura_blanco", label: "ゆらゆらブランコ", page: 180, age: "3〜4歳" },
      { id: "burasagari_gaman", label: "ぶら下がり我慢くらべ", page: 124, age: "3〜4歳" },
      { id: "osaru_janken", label: "おサルさんのジャンケン", page: 189, age: "4〜5歳" },
      { id: "maemawari_ori", label: "前回り下り", page: 193, age: "4〜5歳" },
      { id: "minomushi_gokko", label: "ミノムシごっこ", page: 83, age: "2歳" },
      { id: "osarusan_gokko", label: "おサルさんごっこ", page: 91, age: "2歳" },
      { id: "burasagatte_ashiuchi", label: "ぶら下がって足打ち", page: 96, age: "2歳" },
      { id: "oni_tsukamaero", label: "オニをつかまえろ！", page: 87, age: "2歳" },
      { id: "tunnel_niramekko", label: "トンネルにらめっこ", page: 84, age: "2歳" },
      { id: "ashi_no_aida_catch", label: "足の間でキャッチ！", page: 91, age: "2歳" },
      { id: "osaru_no_akachan", label: "おサルの赤ちゃん", page: 94, age: "2歳" },
      { id: "kumo_no_sanpo", label: "クモの散歩", page: 96, age: "2歳" },
    ],
  },
  {
    id: "nawatobi",
    label: "なわ跳びへの入口",
    force: "跳躍力・協応性",
    items: [
      { id: "nawa_tooshi", label: "なわ通し", page: 153, age: "3〜4歳" },
      { id: "kaiten_nawatobi", label: "回転なわ跳び", page: 153, age: "3〜4歳" },
      { id: "yoko_hebi_jump", label: "横ヘビジャンプ", page: 154, age: "3〜4歳" },
      { id: "hoop_tobi", label: "フープ跳び", page: 215, age: "4〜5歳" },
    ],
  },
  {
    id: "mohou",
    label: "模倣体操・まねっこ",
    force: "まねる力・協応性",
    items: [
      { id: "jump_de_hakushu", label: "ジャンプで拍手", page: 169, age: "3〜4歳" },
      { id: "pose_wa_nanikana", label: "ポーズは何かな？", page: 77, age: "2歳" },
      { id: "hurdle_koete_skip", label: "ハードル越えてスキップ", page: 232, age: "4〜5歳" },
      { id: "ashi_no_yubi_gupa", label: "足の指をグーパー", page: 77, age: "2歳" },
    ],
  },
  {
    id: "ladder",
    label: "反復横跳び・ラダー",
    force: "瞬発力・脚力",
    items: [
      { id: "zigzag_hashiri", label: "ジグザグ走り", page: 121, age: "3〜4歳" },
      { id: "hoop_korokoro", label: "フープコロコロ", page: 211, age: "3〜4歳" },
      { id: "yokomuki_zigzag_tobi", label: "横向きジグザグ跳び", page: 148, age: "4〜5歳" },
      { id: "osumou_asobi", label: "おすもうあそび", page: 83, age: "2歳" },
    ],
  },
  {
    id: "kakekko",
    label: "かけっこ・走ってとまる",
    force: "脚力・切り替え",
    items: [
      { id: "dotabata_usagi", label: "ドタバタウサギ", page: 102, age: "3〜4歳" },
      { id: "hebi_kara_nigero", label: "ジャンプでヘビから逃げろ！", page: 92, age: "2歳" },
      { id: "kisha_kisha_hashire", label: "汽車、汽車、走れ", page: 228, age: "3〜4歳" },
      { id: "netamama_janken", label: "寝たままジャンケン", page: 115, age: "3〜4歳" },
    ],
  },
  {
    id: "oyako_fureai",
    label: "2歳の親子あそび（ふれあい）",
    force: "柔軟性・高所感覚・協調性・想像力",
    items: [
      { id: "senaka_guin", label: "背中をぐいーん", page: 88, age: "2歳" },
      { id: "senaka_suberidai", label: "背中すべり台", page: 94, age: "2歳" },
      { id: "madamada_noreruyo", label: "まだまだ乗れるよ！", page: 78, age: "2歳" },
      { id: "towel_densha_toorinuke", label: "タオル電車で通り抜け", page: 81, age: "2歳" },
      { id: "onaji_iro_ball_doko", label: "同じ色のボールはどこ？", page: 86, age: "2歳" },
    ],
  },
  {
    id: "renzoku",
    label: "連続運動",
    force: "覚えて切り替える力",
    items: [{ id: "renzoku_course", label: "その日の遊び 2〜3 個をコースにして通す" }],
  },
];

export const MOTOR_ITEMS: readonly (MotorItem & { groupId: string })[] = MOTOR_GROUPS.flatMap(
  (g) => g.items.map((i) => ({ ...i, groupId: g.id })),
);

export function motorItemLabel(id: string): string {
  return MOTOR_ITEMS.find((i) => i.id === id)?.label ?? id;
}

export function motorItemPage(id: string): number | undefined {
  return MOTOR_ITEMS.find((i) => i.id === id)?.page;
}
